import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldCheck, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import '../admin/Auth.css';

const css = `
.auth-promo {
  background-color: #f7f2ef;
}
.auth-promo__overlay {
  background: linear-gradient(
    160deg,
    rgba(10,10,15,0.45) 0%,
    rgba(10,10,15,0.78) 60%,
    rgba(200,16,46,0.65) 100%
  );
}
.auth-promo__headline {
  color: #ffffff;
}
.auth-promo__headline em {
  color: #ffffff;
}
.auth-promo__sub {
  color: rgba(255,255,255,0.9);
}
.auth-promo__tag {
  background: rgba(255,255,255,0.88);
  color: #1f2937;
  border-color: rgba(209,213,219,0.8);
}
.auth-promo__headline {
  color: #111827;
}
.auth-promo__headline em {
  background: linear-gradient(90deg, #111827, #c8102e);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.auth-promo__sub {
  color: #475569;
}
.auth-stat__lbl {
  color: #64748b;
}
`;

export default function SupplierActivate() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [supplierInfo, setSupplierInfo] = useState(null);
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const email = searchParams.get('email') || '';
        const res = await api.get(`/auth/supplier/activation-info/${slug}`, { params: { email } });
        setSupplierInfo(res.data);
      } catch {
        setInvalid(true);
      } finally {
        setChecking(false);
      }
    })();
  }, [slug, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error('Passwords do not match');
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');
    setLoading(true);
    try {
      const email = searchParams.get('email') || supplierInfo?.email || '';
      await api.post('/auth/supplier/activate', { slug, password: form.password, email });
      toast.success('Account activated successfully! Please sign in to continue.');
      navigate('/supplier/login');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Activation failed');
    } finally {
      setLoading(false);
    }
  };

  if (checking) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh'}}>
      <div className="spinner spinner-dark" style={{width:32,height:32}} />
    </div>
  );

  return (
    <div className="auth-layout">
      <style>{css}</style>
      <div className="auth-promo auth-promo--supplier">
        <div className="auth-promo__overlay" />
        <div className="auth-promo__content">
          <div className="auth-promo__logo">
            <img src="/chi-logo.png" alt="CHI Laminate Artflow" className="auth-logo-img" />
          </div>
          <div className="auth-promo__text" style={{ color: '#fff' }}>
            <span className="auth-promo__tag">Account Setup</span>
            <h1 className="auth-promo__headline" style={{ color: '#fff' }}>Activate<br />Your<br /><em style={{ color: '#fff' }}>Account</em></h1>
            <p className="auth-promo__sub" style={{ color: '#fff' }}>
              Set your password and activate your supplier access for Laminate Artflow Approval.
            </p>
          </div>
        </div>
      </div>

      <div className="auth-form-panel">
        <div className="auth-form-wrap">
          <div className="auth-form-logo-sm">
            <img src="/chi-logo.png" alt="CHI Laminate Artflow" className="auth-logo-img" />
          </div>

          {invalid ? (
            <div style={{textAlign:'center', padding:'40px 0'}}>
              <div style={{width:64,height:64,borderRadius:'50%',background:'var(--danger-light)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px',color:'var(--danger)'}}>
                <ShieldCheck size={28} />
              </div>
              <h3 style={{marginBottom:8}}>Invalid or Expired Link</h3>
              <p style={{color:'var(--gray-500)',fontSize:'0.9rem',marginBottom:24}}>
                This activation link is no longer valid. Please contact your supplier admin for a new link.
              </p>
              <a href="/supplier/login" className="btn btn-secondary">Back to Login</a>
            </div>
          ) : (
            <>
              <div className="auth-form-header">
                <div className="auth-activate-icon">
                  <ShieldCheck size={28} />
                </div>
                <h2 className="auth-form-title">Set Your Password</h2>
                <p className="auth-form-sub">
                  Welcome, <strong>{supplierInfo?.name}</strong>! Create a password to activate your supplier account.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" type="email" value={supplierInfo?.email || ''} disabled
                    style={{background:'var(--gray-50)', color:'var(--gray-500)'}} />
                </div>

                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <div className="input-password-wrap">
                    <input className="form-input" type={showPass ? 'text' : 'password'}
                      placeholder="Choose a strong password"
                      value={form.password} onChange={e => setForm({...form, password: e.target.value})}
                      required minLength={6} />
                    <button type="button" className="input-eye-btn" onClick={() => setShowPass(!showPass)}>
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Confirm Password</label>
                  <input className="form-input" type="password" placeholder="Repeat your password"
                    value={form.confirm} onChange={e => setForm({...form, confirm: e.target.value})}
                    required />
                </div>

                <button type="submit" className="btn btn-primary btn-lg w-full auth-submit-btn" disabled={loading}>
                  {loading ? <span className="spinner" /> : 'Activate Account & Sign In'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
